import ModalWrapper from '@Components/Modals/ModalWrapper/ModalWrapper';
import AssetStatusServices from '@Services/AssetStatus.services.ts';
import { AssetStatus } from '@Types/Asset.types';
import { NewAssetStatusFields } from '@Types/Form.types';
import { formatRequired } from '@Utils/form.utils';
import { handleRequestError } from '@Utils/request.utils';
import { VALIDATION_RULES } from '@Utils/rule.utils.ts';
import { ErrorDisplay, FreeText, Toast } from '@kg-internal/pv3-uikit';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

type Props = {
  show: boolean;
  assetStatus?: AssetStatus;
  onClose: () => void;
  onSuccess: () => void;
};

const { ASSET_STATUS } = VALIDATION_RULES;

const AssetStatusModal = ({ show, assetStatus, onClose, onSuccess }: Props) => {
  const {
    handleSubmit,
    formState: { errors },
    reset,
    register,
    setValue,
  } = useForm<NewAssetStatusFields>();

  useEffect(() => {
    reset();
    if (assetStatus) {
      setValue('description', assetStatus.description);
    }
  }, [assetStatus, show]);

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const onSubmitSuccess = () => {
    Toast.success(assetStatus ? 'Asset status updated successfully.' : 'Asset status created successfully.');
    onSuccess();
    reset();
    onClose();
  };

  const createTaxStatus = (fields: NewAssetStatusFields) => {
    AssetStatusServices.createAssetStatus(fields)
      .then(() => onSubmitSuccess())
      .catch((errorResponse) => handleRequestError(errorResponse, setErrorMessage, true));
  };

  const updateTaxStatus = (fields: NewAssetStatusFields) => {
    AssetStatusServices.updateAssetStatus(assetStatus?.id, fields)
      .then(() => onSubmitSuccess())
      .catch((errorResponse) => handleRequestError(errorResponse, setErrorMessage, true));
  };

  const title = assetStatus ? 'Edit Asset Status' : 'Add Asset Status';
  const handleAssetStatusSubmit = assetStatus ? updateTaxStatus : createTaxStatus;

  return (
    <div className='modal--fit-content modal--w-30vw modal--pt-25vh'>
      <ModalWrapper
        title={title}
        show={show}
        handleClose={onClose}
        primaryButtonText='Confirm'
        primaryAction={() => handleSubmit(handleAssetStatusSubmit)()}>
        {errorMessage ? (
          <ErrorDisplay inline details={errorMessage} />
        ) : (
          <form onSubmit={() => handleSubmit(handleAssetStatusSubmit)()}>
            <div className='grid'>
              <div className='col-12'>
                <FreeText
                  label={formatRequired('Description')}
                  fullWidth
                  error={!!errors.description}
                  errorMessage={errors.description?.message}
                  {...register('description', ASSET_STATUS.DESCRIPTION)}
                />
                <br />
              </div>
            </div>
          </form>
        )}
      </ModalWrapper>
    </div>
  );
};

export default AssetStatusModal;
